import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Envelope, Lock, Trash, ArrowSquareOut, Chair } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useTeamStore } from '@/stores/teamStore'
import { useBillingStore } from '@/stores/billingStore'
import type { TeamMember } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { usePermission } from '@/hooks/usePermission'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { formatCents, titleizeKey } from '@/lib/billingFormat'
import { describeSeatUsage, extraSeatsPurchasable, seatCeilingMessage, seatPurchaseTotalCents } from '@/lib/seats'
import {
  labelClass,
  labelStyle,
  cardChromeClass,
  iconChipClass,
  iconChipAmberClass,
  iconChipNeutralClass,
} from '@/lib/styleKit'

type Role = 'owner' | 'admin' | 'manager' | 'representative' | 'analyst'

const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  representative: 'Representative',
  analyst: 'Analyst',
}

// Every role BUT owner is a valid target when inviting or reassigning: an
// Owner is granted only by an existing Owner (see team.py's structural
// self-escalation guard), never picked from this generic list.
const ASSIGNABLE_ROLES: Role[] = ['admin', 'manager', 'representative', 'analyst']
const roleOptions = (roles: Role[]) => roles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))

export default function TeamSection() {
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)
  const {
    members, invites, inviteEligibility, seatStatus, loading,
    fetchMembers, fetchInvites, fetchInviteEligibility, fetchSeatStatus,
    createInvite, revokeInvite, updateMemberRole, removeMember, transferOwnership,
  } = useTeamStore()
  const addSeats = useBillingStore((s) => s.addSeats)

  // Everyone in the account may READ the roster (users.view); only Owner/
  // Admin may change it. The three write cells below are what gate the
  // invite form, the role dropdowns and the remove buttons.
  const { allowed: canViewMembers } = usePermission('users', 'view')
  const { allowed: canInvite } = usePermission('users', 'invite')
  const { allowed: canAssignRole } = usePermission('users', 'assign_role')
  const { allowed: canRemove } = usePermission('users', 'remove')
  // Owner only (spec §4-A "Transfer ownership": Owner Manage, everyone else
  // —). This is the flow both last-Owner guards tell people to use and which
  // did not exist until AB#22363.
  const { canWrite: canTransferOwnership } = usePermission('billing', 'transfer_ownership')

  // Which member a pending transfer would hand the account to, or null.
  const [pendingTransfer, setPendingTransfer] = useState<TeamMember | null>(null)
  const [transferring, setTransferring] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('representative')
  const [checkingEligibility, setCheckingEligibility] = useState(canInvite)
  // "Buy more seats" (Momentum per-seat spec §5): opens when every paid seat
  // is taken and the plan still has room below its ceiling. Holds the number
  // of ADDITIONAL seats being bought; null = closed.
  const [buyingSeats, setBuyingSeats] = useState<number | null>(null)
  const [buySeatsBusy, setBuySeatsBusy] = useState(false)
  // Owner/Admin may buy seats (billing.manage_subscription "manage"); the
  // invite gate alone (users.invite) is not enough to spend money.
  const { canWrite: canBuySeats } = usePermission('billing', 'manage_subscription')
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()

  useEffect(() => {
    if (canViewMembers) fetchMembers().catch(() => {})
  }, [canViewMembers, fetchMembers])

  // Pending invites and the plan's seat eligibility are only meaningful to
  // whoever can actually invite — fetching them as a Rep would just 403.
  useEffect(() => {
    if (!canInvite) return
    fetchInvites().catch(() => {})
    fetchSeatStatus().catch(() => {})
    setCheckingEligibility(true)
    fetchInviteEligibility().finally(() => setCheckingEligibility(false))
  }, [canInvite, fetchInvites, fetchInviteEligibility, fetchSeatStatus])

  // The three outcomes of "Add member" (spec §5), decided from the last seat
  // status we have. When it is unknown the invite is simply sent and the
  // server's own answer (402 / 403) drives the same branches.
  const seatsFull = seatStatus !== null && seatStatus.per_seat_billing && seatStatus.seats_available <= 0
  const atCeiling = seatStatus !== null && seatStatus.at_plan_ceiling
  const canStillBuy = seatStatus !== null && seatStatus.can_buy_more
  const maxExtraSeats = seatStatus ? extraSeatsPurchasable(seatStatus) : 0

  const openBuySeats = () => setBuyingSeats(Math.min(1, Math.max(1, maxExtraSeats)))

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    if (seatsFull && !atCeiling && canStillBuy && canBuySeats) {
      openBuySeats()
      return
    }
    try {
      await createInvite({ email: email.trim(), role })
      setEmail('')
      setRole('representative')
    } catch (error: any) {
      // 402 = every paid seat is in use; buying more is the way forward and
      // the store deliberately did not toast it. Anything else was toasted.
      if (error?.response?.status === 402 && canBuySeats) {
        fetchSeatStatus().catch(() => {})
        openBuySeats()
      }
    }
  }

  const confirmBuySeats = async () => {
    if (!buyingSeats) return
    setBuySeatsBusy(true)
    try {
      const ok = await addSeats(buyingSeats)
      if (ok) {
        setBuyingSeats(null)
        await fetchSeatStatus().catch(() => {})
      }
    } finally {
      setBuySeatsBusy(false)
    }
  }

  const seatRateCents = seatStatus
    ? (seatStatus.billing_cycle === 'annual'
        ? seatStatus.annual_seat_price_cents ?? seatStatus.seat_price_cents
        : seatStatus.seat_price_cents)
    : null

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Team
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          {canInvite
            ? 'Invite teammates and manage who has access to your account.'
            : 'See who is on your team. Only an Owner or Admin can change roles or access.'}
        </p>
      </div>

      {/* Your role: visible to every role, always. */}
      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipNeutralClass}>
              <Users className="w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" />
            </div>
            <div>
              <CardTitle>Your role</CardTitle>
              <CardDescription>What your account can do here</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <span
            className="inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full border bg-[#5e6ad2]/[0.08] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]"
            style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
          >
            {currentUser?.role ? ROLE_LABELS[currentUser.role as Role] ?? currentUser.role : 'Unknown'}
          </span>
        </CardContent>
      </Card>

      {/* Inviting and its plan gate are Owner/Admin-only (users.invite); the
          backend refuses them for everyone else, so they aren't rendered. */}
      {canInvite && (
        <>
          {/* Invite a teammate: plan-gated (Momentum/Enterprise). Nothing renders
              while the eligibility check is in flight, so the locked card
              never flashes before flipping to the real form. */}
          {!checkingEligibility && (
            inviteEligibility?.eligible ? (
              <Card className={cardChromeClass}>
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <div className={iconChipClass}>
                      <Envelope className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                    </div>
                    <div>
                      <CardTitle>Invite a teammate</CardTitle>
                      <CardDescription>They'll get an email with a link to join your account</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Paid vs occupied seats (spec §5). Only a per-seat plan has
                      anything to count; Enterprise is unlimited. */}
                  {seatStatus?.per_seat_billing && (
                    <div
                      className="flex items-center justify-between gap-3 flex-wrap px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08]"
                      data-testid="seat-summary"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Chair className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0" weight="fill" />
                        <div className="min-w-0">
                          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                            {describeSeatUsage(seatStatus)}
                          </p>
                          <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                            {atCeiling
                              ? seatCeilingMessage(seatStatus)
                              : seatsFull
                                ? 'Every paid seat is taken. Buy more seats to invite another teammate.'
                                : `${seatStatus.seats_available} seat${seatStatus.seats_available === 1 ? '' : 's'} free — invite without any extra charge.`}
                            {seatStatus.pending_paid_seats !== null && (
                              <> Dropping to {seatStatus.pending_paid_seats} seats at the next renewal.</>
                            )}
                          </p>
                        </div>
                      </div>
                      {seatsFull && !atCeiling && canStillBuy && canBuySeats && (
                        <Button variant="outline" size="sm" onClick={openBuySeats}>
                          Buy more seats
                        </Button>
                      )}
                      {atCeiling && canBuySeats && (
                        <Button variant="outline" size="sm" onClick={() => navigate('/billing')}>
                          <ArrowSquareOut className="w-4 h-4 mr-1.5" weight="bold" />
                          Talk to us
                        </Button>
                      )}
                    </div>
                  )}
                  <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1">
                      <label htmlFor="invite-email" className={labelClass} style={labelStyle}>Email</label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="teammate@company.com"
                      />
                    </div>
                    <div className="sm:w-40 shrink-0">
                      <label htmlFor="invite-role" className={labelClass} style={labelStyle}>Role</label>
                      <Select
                        id="invite-role"
                        value={role}
                        onChange={(v) => setRole(v as Role)}
                        options={roleOptions(ASSIGNABLE_ROLES)}
                        minWidth={150}
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="default"
                      size="sm"
                      loading={loading}
                      disabled={planLapsed || !email.trim() || atCeiling || (seatsFull && !(canStillBuy && canBuySeats))}
                      title={lapsedTitle}
                      className="shrink-0"
                    >
                      {seatsFull && !atCeiling && canStillBuy && canBuySeats ? 'Buy seats & invite' : 'Send invite'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className={cardChromeClass}>
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <div className={iconChipAmberClass}>
                      <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
                    </div>
                    <div>
                      <CardTitle>Invite a teammate</CardTitle>
                      <CardDescription>Available on the Momentum and Enterprise plans</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
                      {/* titleizeKey so the raw slug never reaches the reader:
                          this rendered "on the standard plan" before the slugs
                          were renamed, and would now read "starter". */}
                      {inviteEligibility?.plan_name
                        ? `Your account is on the ${titleizeKey(inviteEligibility.plan_name)} plan. Upgrade to Momentum or Enterprise to add teammates.`
                        : 'Upgrade to Momentum or Enterprise to add teammates.'}
                    </p>
                    {/* Same reason as the seat-ceiling button above: whoever
                        cannot buy cannot upgrade, and Billing is hidden from
                        them. The sentence above still says what is needed. */}
                    {canBuySeats && (
                      <Button variant="outline" size="sm" onClick={() => navigate('/billing')}>
                        <ArrowSquareOut className="w-4 h-4 mr-1.5" weight="bold" />
                        Upgrade plan
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          )}

          {/* Pending invites */}
          {invites.length > 0 && (
            <Card className={cardChromeClass}>
              <CardHeader>
                <CardTitle>Pending invites</CardTitle>
                <CardDescription>Invites that haven't been accepted yet</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08]"
                  >
                    <div className="min-w-0">
                      <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                        {invite.email}
                      </p>
                      <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                        Invited as {ROLE_LABELS[invite.role as Role] ?? invite.role}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revokeInvite(invite.id)}>
                      Revoke
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

        </>
      )}

      {/* Team members: readable by EVERY role (users.view) — knowing who is
          on the team, and who to ask for access, isn't an administrative act.
          The controls inside stay on their own write cells, so a Manager,
          Rep or Analyst sees the roster and nothing that changes it. */}
      {canViewMembers && (
        <Card className={cardChromeClass}>
          <CardHeader>
            <CardTitle>Team members</CardTitle>
            <CardDescription>Everyone with access to this account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {members.map((member) => {
              const isSelf = member.id === currentUser?.id
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] flex-wrap"
                >
                  <div className="min-w-0">
                    <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                      {member.full_name}{isSelf && <span className="text-muted-foreground dark:text-[#8a8f98]"> (you)</span>}
                    </p>
                    <p className="text-muted-foreground dark:text-[#8a8f98] truncate" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {member.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* An Owner's role can only change via a deliberate ownership
                        transfer (team.py's structural guard), never offered here
                        as a plain dropdown option, and never editable on yourself.
                        Without assign_role the role shows as a plain chip. */}
                    {canAssignRole && member.role !== 'owner' && !isSelf ? (
                      <Select
                        value={member.role}
                        onChange={(v) => updateMemberRole(member.id, v)}
                        options={roleOptions(ASSIGNABLE_ROLES)}
                        minWidth={150}
                        ariaLabel={`Role for ${member.full_name}`}
                      />
                    ) : (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full border bg-muted/60 border-border/50 text-foreground dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-[#f7f8f8]"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        {ROLE_LABELS[member.role as Role] ?? member.role}
                      </span>
                    )}
                    {canTransferOwnership && !isSelf && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPendingTransfer(member)}
                        aria-label={`Make ${member.full_name} the owner`}
                      >
                        Make owner
                      </Button>
                    )}
                    {canRemove && member.role !== 'owner' && !isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(member.id)}
                        aria-label={`Remove ${member.full_name}`}
                      >
                        <Trash className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Buy more seats (spec §5/§7): prorated for the rest of the period and
          charged now; the new seats are free to fill immediately. Capped so
          the total never passes the Momentum ceiling. */}
      <ConfirmDialog
        open={buyingSeats !== null}
        title="Buy more seats"
        description={
          buyingSeats !== null && seatStatus ? (
            <div className="space-y-3">
              <p>
                You are paying for {seatStatus.paid_seats} seat{seatStatus.paid_seats === 1 ? '' : 's'} and all of them are
                taken. Momentum allows up to {seatStatus.max_seats ?? 5}; how many more do you need?
              </p>
              <div className="flex items-center gap-3">
                <label htmlFor="extra-seats" className={labelClass} style={labelStyle}>Additional seats</label>
                <Input
                  id="extra-seats"
                  type="number"
                  min={1}
                  max={maxExtraSeats}
                  value={buyingSeats}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    setBuyingSeats(Number.isNaN(n) ? 1 : Math.min(maxExtraSeats, Math.max(1, n)))
                  }}
                  className="w-24"
                />
              </div>
              {seatRateCents !== null && seatRateCents !== undefined && (
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px' }}>
                  {formatCents(seatRateCents)} per seat per month — your recurring charge becomes{' '}
                  {formatCents(seatPurchaseTotalCents(seatRateCents, seatStatus.paid_seats + buyingSeats))}/month.
                  The remainder of the current period is charged today, prorated.
                </p>
              )}
            </div>
          ) : ''
        }
        confirmLabel={buyingSeats ? `Buy ${buyingSeats} seat${buyingSeats === 1 ? '' : 's'}` : 'Buy'}
        loading={buySeatsBusy}
        onConfirm={confirmBuySeats}
        onCancel={() => !buySeatsBusy && setBuyingSeats(null)}
      />

      {/* Ownership transfer is irreversible from the outgoing Owner's side —
          they come out of it an Admin, and only the new Owner can hand it
          back — so it confirms, and the dialog spells out what moves. */}
      <ConfirmDialog
        open={pendingTransfer !== null}
        title="Transfer ownership"
        description={
          pendingTransfer
            ? `${pendingTransfer.full_name} (${pendingTransfer.email}) becomes the Owner of this account. `
              + 'The subscription and the credit pool move with the ownership, and you become an Admin — '
              + 'you keep settings, integrations, users and senders, but lose billing. '
              + 'Only the new Owner can transfer it back.'
            : ''
        }
        confirmLabel="Transfer ownership"
        tone="destructive"
        loading={transferring}
        onConfirm={async () => {
          if (!pendingTransfer) return
          setTransferring(true)
          try {
            await transferOwnership(pendingTransfer.id)
            setPendingTransfer(null)
          } catch {
            // Error already toasted by the store; keep the dialog open.
          } finally {
            setTransferring(false)
          }
        }}
        onCancel={() => !transferring && setPendingTransfer(null)}
      />
    </div>
  )
}
